import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import BottomNav from './BottomNav';

const API_URL = process.env.REACT_APP_API_URL?.replace('/api','') || '';

const memberNav = [
  { to: '/dashboard',  icon: '📊', label: 'Dashboard' },
  { to: '/checkin',    icon: '📲', label: 'Check-in' },
  { to: '/workouts',   icon: '🏋️', label: 'Workouts' },
  { to: '/nutrition',  icon: '🥗', label: 'Nutrition' },
  { to: '/progress',   icon: '📸', label: 'Progress' },
  { to: '/classes',    icon: '🗓️', label: 'Classes' },
  { to: '/profile',    icon: '👤', label: 'Profile' },
];

const adminNav = [
  { to: '/admin/dashboard',     icon: '📊', label: 'Dashboard' },
  { to: '/admin/members',       icon: '👥', label: 'Members' },
  { to: '/admin/plans',         icon: '💳', label: 'Plans' },
  { to: '/admin/attendance',    icon: '📋', label: 'Attendance' },
  { to: '/admin/classes',       icon: '🗓️', label: 'Classes' },
  { to: '/admin/announcements', icon: '📢', label: 'Announcements' },
];

const s = {
  shell:    { display:'flex', height:'100vh', overflow:'hidden' },
  sidebar:  { width:220, background:'var(--surface)', borderRight:'1px solid var(--border)', display:'flex', flexDirection:'column', height:'100vh', overflow:'auto', flexShrink:0 },
  brand:    { display:'flex', alignItems:'center', gap:10, padding:'20px 18px 14px', borderBottom:'1px solid var(--border)' },
  brandMark:{ width:34, height:34, borderRadius:9, background:'var(--accent)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:17, flexShrink:0 },
  brandName:{ fontFamily:'var(--font-display)', fontSize:16, fontWeight:700, color:'var(--text-primary)' },
  userCard: { margin:'12px 10px', padding:'10px 12px', background:'var(--surface-2)', borderRadius:'var(--radius)', border:'1px solid var(--border)', display:'flex', alignItems:'center', gap:9 },
  nav:      { flex:1, padding:'4px 8px' },
  navSec:   { fontSize:10, fontWeight:600, letterSpacing:'.07em', textTransform:'uppercase', color:'var(--text-muted)', padding:'12px 10px 4px' },
  navItem:  { display:'flex', alignItems:'center', gap:10, padding:'9px 12px', borderRadius:6, color:'var(--text-secondary)', fontSize:13, fontWeight:500, transition:'.12s', textDecoration:'none', marginBottom:2 },
  sideBot:  { padding:'10px 12px', borderTop:'1px solid var(--border)' },
  logoutBtn:{ width:'100%', background:'none', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:'8px 12px', color:'var(--text-secondary)', fontSize:12, fontWeight:500, display:'flex', alignItems:'center', gap:8, transition:'.12s', cursor:'pointer' },
  main:     { flex:1, display:'flex', flexDirection:'column', overflow:'hidden', minWidth:0 },
  topbar:   { height:56, background:'var(--surface)', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', gap:14, padding:'0 18px', flexShrink:0 },
  content:  { flex:1, overflowY:'auto', padding:'20px', paddingBottom:80 },
};

export default function Layout({ isAdmin }) {
  const { user, logout } = useAuth();
  const navigate         = useNavigate();
  const [open, setOpen]  = useState(false);
  const nav              = isAdmin ? adminNav : memberNav;

  const handleLogout = () => { logout(); navigate('/login'); };

  const initials = user?.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0,2);
  const photoSrc = user?.photo ? `${API_URL}${user.photo}` : null;
  const activeStyle = { background:'var(--accent-dim)', color:'var(--accent)' };

  return (
    <>
      <style>{`
        @media (max-width: 767px) {
          .desktop-sidebar { display: none !important; }
          .bottom-nav { display: flex !important; }
          .topbar-desktop-title { display: none; }
        }
        @media (min-width: 768px) {
          .bottom-nav { display: none !important; }
          .mobile-menu-btn { display: none !important; }
        }
      `}</style>

      <div style={s.shell}>
        {/* Mobile overlay */}
        {open && <div onClick={() => setOpen(false)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.6)', zIndex:90 }} />}

        {/* Sidebar — hidden on mobile, shown on desktop */}
        <aside className="desktop-sidebar" style={{
          ...s.sidebar,
          position: 'relative',
          zIndex: 100,
        }}>
          <div style={s.brand}>
            <div style={s.brandMark}>🏋️</div>
            <span style={s.brandName}>Fit to Fit</span>
          </div>

          <div style={s.userCard}>
            <div className="avatar" style={{ width:32, height:32 }}>
              {photoSrc ? <img src={photoSrc} alt={user?.name} /> : initials}
            </div>
            <div>
              <div style={{ fontSize:12, fontWeight:600, color:'var(--text-primary)' }}>{user?.name}</div>
              <div style={{ fontSize:10, color:'var(--text-muted)', textTransform:'capitalize' }}>{user?.role}</div>
            </div>
          </div>

          <nav style={s.nav}>
            <div style={s.navSec}>{isAdmin ? 'Management' : 'My Gym'}</div>
            {nav.map(item => (
              <NavLink key={item.to} to={item.to} style={({ isActive }) => ({ ...s.navItem, ...(isActive ? activeStyle : {}) })}>
                <span style={{ fontSize:16 }}>{item.icon}</span>
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>

          <div style={s.sideBot}>
            <button style={s.logoutBtn} onClick={handleLogout}
              onMouseEnter={e => e.currentTarget.style.background='var(--danger-dim)'}
              onMouseLeave={e => e.currentTarget.style.background='none'}>
              🚪 <span>Sign out</span>
            </button>
          </div>
        </aside>

        {/* Main area */}
        <div style={s.main}>
          {/* Top bar */}
          <header style={s.topbar}>
            {/* Mobile menu button */}
            <button className="mobile-menu-btn" onClick={() => setOpen(true)}
              style={{ background:'none', border:'none', color:'var(--text-secondary)', fontSize:22, cursor:'pointer', padding:4, display:'flex' }}>
              ☰
            </button>

            <span style={{ fontFamily:'var(--font-display)', fontSize:15, fontWeight:700, color:'var(--text-primary)', flex:1 }}>
              {isAdmin ? '⚙️ Admin Panel' : '🏋️ Fit to Fit'}
            </span>

            <div className="avatar">{photoSrc ? <img src={photoSrc} alt="" /> : initials}</div>
          </header>

          {/* Mobile slide-out sidebar */}
          <aside style={{
            position:'fixed', top:0, left:0, height:'100%', width:260,
            background:'var(--surface)', borderRight:'1px solid var(--border)',
            zIndex:100, transform: open ? 'translateX(0)' : 'translateX(-100%)',
            transition:'transform .2s', display:'flex', flexDirection:'column',
          }}>
            <div style={{ ...s.brand, justifyContent:'space-between' }}>
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <div style={s.brandMark}>🏋️</div>
                <span style={s.brandName}>Fit to Fit</span>
              </div>
              <button onClick={() => setOpen(false)} style={{ background:'none', border:'none', color:'var(--text-muted)', fontSize:18, cursor:'pointer' }}>✕</button>
            </div>

            <div style={s.userCard}>
              <div className="avatar" style={{ width:32, height:32 }}>
                {photoSrc ? <img src={photoSrc} alt="" /> : initials}
              </div>
              <div>
                <div style={{ fontSize:12, fontWeight:600, color:'var(--text-primary)' }}>{user?.name}</div>
                <div style={{ fontSize:10, color:'var(--text-muted)', textTransform:'capitalize' }}>{user?.role}</div>
              </div>
            </div>

            <nav style={s.nav}>
              {nav.map(item => (
                <NavLink key={item.to} to={item.to}
                  style={({ isActive }) => ({ ...s.navItem, ...(isActive ? activeStyle : {}) })}
                  onClick={() => setOpen(false)}>
                  <span style={{ fontSize:16 }}>{item.icon}</span>
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </nav>

            <div style={s.sideBot}>
              <button style={s.logoutBtn} onClick={handleLogout}>🚪 <span>Sign out</span></button>
            </div>
          </aside>

          {/* Page content */}
          <main style={s.content}>
            <Outlet />
          </main>
        </div>
      </div>

      {/* Bottom nav — mobile only */}
      <BottomNav isAdmin={isAdmin} />
    </>
  );
}
